/* eslint-disable @nice-cxone/ccf/required-tsdoc */

import classes from './admin.module.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { Dropdown } from 'antd';
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Divider,
  Popper,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Stack,
  Chip,
  FormControlLabel,
  SelectChangeEvent
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import VerticalForm from './verticalForm';
import {
  API_BASE_URL,
  AUTHORIZATION_TOKEN,
  callApi,
  EXPERT_VERTICALS,
  UPADATE_DEMO_STATUS,
  UPDATE_ADMIN_ACCESS,
} from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-copilot/ccf-copilot-api';
import { useMsal } from '@azure/msal-react';
import { useAdminAccess } from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-copilot/useAdminAccess';

export interface AssociatedFile {
  name: string;
  description: string | null;
  lastUpdatedBy: string | null;
  lastUpdatedTime: string | null;
  status: string;
}
export interface FormData {
  id: number;
  name: string;
  persona: string;
  status: string;
  expertName: string;
  key: string;
  secret: string;
  copilotURL: string;
  salesPath: string;
  compliancePath: string;
  knowledgePath: string;
  testQuestion: string;
  conversationDetails: AssociatedFile[];
  demoType: string;
  visibility: string;
  owner: string | null;
  description: string | null;
  lastUpdatedBy: string | null;
  lastUpdatedTime: string | null;
}

type Filter = {
  label: string;
  value: string;
};

type Row = {
  name?: string;
  status?: 'Active' | 'Inactive';
  demoType?: string;
  visibility?: string;
  owner?: string;
};

interface Filters {
  [key: string]: any;
}

const initialFormData: FormData = {
  id: 0,
  name: '',
  persona: '',
  status: '',
  expertName: '',
  key: '',
  secret: '',
  copilotURL: '',
  salesPath: '',
  compliancePath: '',
  knowledgePath: '',
  testQuestion: '',
  conversationDetails: [],
  demoType: '',
  visibility: '',
  owner: null,
  description: null,
  lastUpdatedBy: null,
  lastUpdatedTime: null,
};

const renderStatusIcon = (status: string) => {
  if (status === 'Active') {
    return <CheckCircleOutlineIcon className={classes.activeIcon} />;
  } else {
    return <CancelOutlinedIcon className={classes.inactiveIcon} />;
  }
};

const Admin = () => {
  const { accounts } = useMsal();
  const { adminEmails } = useAdminAccess();
  const [searchText, setSearchText] = useState('');
  const [originalRows, setOriginalRows] = useState<FormData[]>([]);
  const [rows, setRows] = useState<FormData[]>([]);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [email, setEmail] = useState<string>('');
  const [addUserMessage, setAddUserMessage] = useState<string | null>(null);
  const [addUserMessageType, setAddUserMessageType] = useState<
    'success' | 'error' | null
  >(null);
  const [showActiveStatus, setShowActiveStatus] = useState(true);
  const [selectedDemoType, setSelectedDemoType] = useState('');
  const [selectedVisibility, setSelectedVisibility] = useState('');
  const [selectedOwner, setSelectedOwner] = useState('');
  const [filters, setFilters] = useState<Filter[]>([]);
  const [showFilterDropdowns, setShowFilterDropdowns] = useState<boolean>(false);

  const allowedUsernames = [
    'nikita.chaudhari@nice.com',
    'deepali.singhavi@nice.com',
    'pratik.patil@nice.com',
    'Brett.Crutchfield@nice.com',
    'Carmit.DiAndrea@nice.com',
    'andy.marchky@nice.com',
  ];
  const loggedInUser = accounts[0]?.username?.toLowerCase();
  const formatUsername = (email: string) => {
    const [firstName, lastName] = email.split('@')[0].split('.');
    return `${firstName.charAt(0).toUpperCase() + firstName.slice(1)} ${
      lastName.charAt(0).toUpperCase() + lastName.slice(1)
    }`;
  };

  useEffect(() => {
    const updatedFilters: Filter[] = [
      selectedDemoType && { label: 'Demo Type', value: selectedDemoType },
      selectedVisibility && { label: 'Visibility', value: selectedVisibility },
      selectedOwner && { label: 'Owner', value: selectedOwner }, 
    ].filter(Boolean) as Filter[];

    setFilters(updatedFilters);
    applyFilters(originalRows, updatedFilters, showActiveStatus, searchText, showFilterDropdowns);
  }, [selectedDemoType, selectedVisibility, selectedOwner, searchText, showActiveStatus]);

  const matchesFilter = (row: FormData, filter: { label: string; value: string }) => {
    let rowValue: string | null = null;
  
    switch (filter.label) {
      case 'Demo Type':
        rowValue = row.demoType;
        break;
      case 'Visibility':
        rowValue = row.visibility;
        break;
      case 'Owner':
        rowValue = row.owner;
        break;
      default:
        return true; // Ignore unknown filters
    }
  
    return rowValue?.toLowerCase() === filter.value.toLowerCase();
  };

  const filterRows = (
    rows: FormData[],
    filters: Filter[],
    showActiveStatus: boolean,
    searchText: string,
    showFilterDropdowns: boolean
  ): FormData[] => {
    const lowerCasedFilters = filters.map(filter => ({
      label: filter.label,
      value: filter.value.toLowerCase(),
    }));
  
    return (rows || []).filter(row => {
      if (showFilterDropdowns) {
        if ((showActiveStatus && row.status !== 'Active') || (!showActiveStatus && row.status !== 'Inactive')) {
          return false;
        }
      }
  
      if (showFilterDropdowns) {
        if (!lowerCasedFilters.every(filter => matchesFilter(row, filter))) {
          return false;
        }
      }
  
      if (searchText) {
        const lowerSearch = searchText.toLowerCase();
        return (
          row.name?.toLowerCase().includes(lowerSearch) ||
          row.status?.toLowerCase().includes(lowerSearch)
        );
      }
  
      return true;
    });
  };
  
  

  const applyFilters = (originalRows: FormData[], activeFilters: Filter[], showActiveStatus: boolean, searchText: string, showFilterDropdowns: boolean) => {
    setRows(filterRows(originalRows, activeFilters, showActiveStatus, searchText, showFilterDropdowns));
  };
  

  const deleteFilter = (index: number) => {
    setFilters((prev) => {
      const filterToRemove = prev[index];
      const newFilters = prev.filter((_, i) => i !== index);

      switch (filterToRemove?.label) {
        case 'Demo Type':
          setSelectedDemoType('');
          break;
        case 'Visibility':
          setSelectedVisibility('');
          break;
        case 'Owner':
          setSelectedOwner('');
          break;
        default:
          break;
      }
      applyFilters(originalRows, newFilters, showActiveStatus, searchText, showFilterDropdowns);
      return newFilters;
    });
  };


  useEffect(() => {
    callApi(API_BASE_URL + EXPERT_VERTICALS, 'GET')
      .then((response: any) => {
        const transformedData = response.map((item: any, index: number) => ({
          compliancePath: item.compliancePath,
          conversationDetails: item.associatedFiles.map(
            ({
              name,
              description,
              lastUpdatedBy,
              lastUpdatedTime,
              status,
            }: AssociatedFile) => ({
              name,
              description,
              lastUpdatedBy,
              lastUpdatedTime,
              status,
            })
          ),
          copilotURL: item.expertCopilotURL,
          expertName: item.expertPathName,
          id: index + 1,
          key: item.expertCopilotClientKey,
          knowledgePath: item.kBAnswersPath,
          name: item.expertSystem,
          persona: item.persona,
          salesPath: item.salesOffersPath,
          secret: item.expertCopilotClientSecret,
          status: item.status,
          demoType: item.demoType,
          visibility: item.visibility,
          owner: item.owner,
          description: item.description,
          lastUpdatedBy: item.lastUpdatedBy,
          lastUpdatedTime: item.lastUpdatedTime,
        }));

        const isPublicOrOwner = (item: any) => {
          const isPublic = item.visibility === 'Public';
          const isOwner = item.owner === formatUsername(loggedInUser);
          return isPublic || isOwner;
        };

        const filteredData = transformedData.filter(isPublicOrOwner);

        setOriginalRows(filteredData);
        setRows(filteredData);
      })
      .catch((error) => {
        console.error(
          'Error fetching expert verticals from API:',
          error.message
        );
      });
  }, [loggedInUser]);

  const handleFormDataChange = (
    name: string,
    value: string | AssociatedFile[]
  ) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleEditMenuClick = useCallback((rowData: FormData) => {
    setFormData(rowData);
    setMode('edit');
    setIsFormVisible(true);
  }, []);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toLowerCase();
    setSearchText(value); 
    applyFilters(rows, filters,showActiveStatus, value, showFilterDropdowns);
  };
  
  const handleDemoTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedDemoType(value);
    applyFilters(originalRows,[...filters, { label: 'Demo Type', value }], showActiveStatus, searchText, showFilterDropdowns);
  };
  
  const handleVisibilityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedVisibility(value);
    applyFilters(originalRows, [...filters, { label: 'Visibility', value }], showActiveStatus, searchText, showFilterDropdowns);
  };

  const handleOwnerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedOwner(value);
    applyFilters(originalRows, [...filters, { label: 'Owner', value }], showActiveStatus, searchText, showFilterDropdowns);
  };
  
  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    setShowActiveStatus(value);
    applyFilters(originalRows, filters, value, searchText, showFilterDropdowns);
  };

  const handleCreateButtonClick = () => {
    setMode('create');
    setFormData(initialFormData);
    setIsFormVisible(true);
  };

  const handleBackToList = () => {
    setIsFormVisible(false);
  };

  const handleFilterClick = () => {
    setShowFilterDropdowns((prev) => !prev);
    
  };

  useEffect(() => {
    handleClearAllFilters(); 
  }, [showFilterDropdowns])

  const handleToggleStatus = async (rowData: FormData) => {
    const updatedStatus = rowData.status === 'Active' ? 'Inactive' : 'Active';
    const updatedRowData = { ...rowData, status: updatedStatus };

    setRows((prevRows) =>
      prevRows.map((row) => (row.id === rowData.id ? updatedRowData : row))
    );
    setOriginalRows((prevRows) =>
      prevRows.map((row) => (row.id === rowData.id ? updatedRowData : row))
    );
    try {
      const response = await fetch(`${API_BASE_URL}${UPADATE_DEMO_STATUS}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: AUTHORIZATION_TOKEN,
        },
        body: JSON.stringify({
          expertPathName: rowData.expertName,
          status: updatedStatus,
        }),
      });
        if (!response.ok) {
        throw new Error('Failed to update status');
      }

    } catch (error) {
      console.error('Error updating status:', error);
    }
    applyFilters(originalRows, filters, showActiveStatus, searchText, showFilterDropdowns);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Demo Name',
        width: 170,
      },
      {
        field: 'demoType',
        headerName: 'Demo Type',
        width: 200,
      },
      {
        field: 'visibility',
        headerName: 'Visibility',
        width: 200,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 200,
        renderCell: ({ value }) => (
          <>
            {renderStatusIcon(value as string)}
            <span style={{ marginLeft: 8 }}>{value}</span>
          </>
        ),
      },
      {
        field: 'owner',
        headerName: 'Owner',
        width: 200,
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        disableColumnMenu: true,
        sortable: false,
        renderCell: ({ row }) => {
          const menuItems = [
            {
              key: 'rowAction-1',
              icon: <EditIcon />,
              label: 'Edit',
              onClick: () => handleEditMenuClick(row),
            },
            {
              key: 'rowAction-2',
              icon: row.status === 'Active' ? <CancelIcon className={classes.inactiveIcon} /> : <CheckCircleIcon className={classes.activeIcon} />,
              label: row.status === 'Active' ? 'Deactivate' : 'Activate',
              onClick: () => handleToggleStatus(row),
            },
          ];

          return (
            <Dropdown
              menu={{
                items: menuItems,
              }}
              trigger={['click']}
            >
              <Button
                startIcon={<MoreVertIcon />}
                className={classes.actionIcon}
              />
            </Dropdown>
          );
        },
      },
    ],
    [handleEditMenuClick]
  );

  const datagridBottomBorder = '1px solid rgba(236, 233, 233, 1)';

  const handleManageUsersClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setAddUserMessage(null);
    setAddUserMessageType(null);
    setEmail('');
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setAddUserMessage('');
  };

  const handleGrantAdminAccess = async () => {
    const params = {
      EmailAddress: email,
      lastModifiedBy: loggedInUser,
      lastModifiedTime: new Date().toISOString(),
    };

    try {
      const response = await fetch(`${API_BASE_URL}${UPDATE_ADMIN_ACCESS}`, {
        method: 'POST',
        headers: {
          Authorization: AUTHORIZATION_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      const data = await response.text();

      if (!response.ok) {
        throw new Error(data);
      }

      setAddUserMessage(data);
      setAddUserMessageType('success');
      setEmail('');

      setTimeout(() => {
        setAddUserMessage(null);
        setAddUserMessageType(null);
      }, 5000);
    } catch (error: any) {
      setAddUserMessage(error.message);
      setAddUserMessageType('error');
    }
  };
  const handleClosePopper = () => {
    setAnchorEl(null);
    setAddUserMessage(null);
    setAddUserMessageType(null);
    setEmail('');
  };

  const handleClearAllFilters = (): void => {
    resetFilters();
    let filteredRows: Row[] = originalRows as Row[];
  
    if (searchText) {
      filteredRows = filterRowsBySearchText(filteredRows);
      updateActiveStatus(filteredRows);
      setRows(filteredRows as unknown as FormData[]);
    } else if (!searchText && !showFilterDropdowns) {
      resetToActiveRows();
      setRows(filteredRows as unknown as FormData[]);
    } else {
      applyFilters(originalRows, filters, showActiveStatus, searchText, showFilterDropdowns);
    }
  };
  
  const resetFilters = (): void => {
    setSelectedDemoType('');
    setSelectedOwner('');
    setSelectedVisibility('');
  };
  
  const filterRowsBySearchText = (rows: Row[]): Row[] => {
    return rows.filter((row) => {
      const matchesSearch =
        (row.name?.toLowerCase().includes(searchText) ?? false) ||
        (row.status?.toLowerCase().includes(searchText) ?? false);
  
      if (showFilterDropdowns) {
        return matchesSearch && (showActiveStatus ? row.status === 'Active' : row.status === 'Inactive');
      }
      return matchesSearch;
    });
  };
  
  const updateActiveStatus = (filteredRows: Row[]): void => {
    const allActive = filteredRows.every((row) => row.status === 'Active');
    setShowActiveStatus(allActive); 
  };
  
  const resetToActiveRows = (): void => {
    setShowActiveStatus(true); 
  };
  
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  return (
    <div className={classes.container}>
      {isFormVisible ? (
        <VerticalForm
          formData={formData}
          handleBack={handleBackToList}
          handleFormDataChange={handleFormDataChange}
          mode={mode}
          setIsFormVisible={setIsFormVisible}
          setRows={setRows}
          rows={rows}
          setOriginalRows={setOriginalRows}
          originalRows={originalRows}
        />
      ) : (
        <>
          <Helmet>
            <title>CXone Agent Admin</title>
          </Helmet>
          <div className={classes.header}>
            <p className={classes.title}>List of Demos</p>
            <div className={classes.buttonContainer}>
              {allowedUsernames.includes(loggedInUser) && (
                <Button
                  variant="contained"
                  className={classes.usersButton}
                  onClick={handleManageUsersClick}
                  size="small"
                >
                  Manage Users
                </Button>
              )}
              <Popper id={id} open={open} anchorEl={anchorEl}>
                <Paper className={classes.popper}>
                  <Box p={2}>
                    <IconButton
                      size="small"
                      onClick={handleClosePopper}
                      className={classes.closeButton}
                    >
                      <CloseIcon />
                    </IconButton>
                    <Typography
                      variant="h6"
                      className={classes.adminAccessHeader}
                    >
                      Grant Admin Access
                    </Typography>
                    <TextField
                      label="Email Address"
                      value={email}
                      onChange={handleEmailChange}
                      fullWidth
                      size="small"
                      style={{ marginTop: '10px' }}
                    />
                    {addUserMessage && (
                      <Typography
                        variant="body2"
                        color={
                          addUserMessageType === 'success' ? 'green' : 'red'
                        }
                        className={classes.addUserMessage}
                      >
                        {addUserMessage}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleGrantAdminAccess}
                      className={classes.addUserButton}
                      size="small"
                    >
                      Add User
                    </Button>
                  </Box>
                </Paper>
              </Popper>
              <Button
                variant="contained"
                className={classes.button}
                onClick={handleCreateButtonClick}
                size="small"
              >
                Create New Demo
              </Button>
            </div>
          </div>
          <div className={classes.listing}>
            <Box className={classes.searchAndFilterContainer}>
              <TextField
                variant="outlined"
                placeholder="Search"
                size="small"
                fullWidth
                value={searchText}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
               className={classes.filterIcon}
               onClick={() => handleFilterClick()}
               size="medium"
                
              >
                {showFilterDropdowns ? 
                 <FilterAltIcon 
                  className={classes.filledIcon}
                 />
                :
               <FilterAltOutlinedIcon 
               className={classes.filledIcon}
               /> 
              }
              </IconButton>
            </Box>
            {showFilterDropdowns ?
             <>
              <FormControl
              size="small"
              className={classes.demoTypeDropdown}
            >
              <InputLabel>Select Demo Type</InputLabel>
              <Select
                value={selectedDemoType}
                placeholder="Select Demo Type"
                onChange={(event: SelectChangeEvent) => {
                  handleDemoTypeChange(event as React.ChangeEvent<HTMLInputElement>);
                }}
                label="Select Demo Type"
              >
                <MenuItem value="vertical">Vertical</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" className={classes.visibilityOwnerDropdown}>
              <InputLabel>Select Visibility</InputLabel>
              <Select
                value={selectedVisibility}
                placeholder="Select Visibility"
                onChange={(event: SelectChangeEvent) => {
                  handleVisibilityChange(event as React.ChangeEvent<HTMLInputElement>);
                }}
                label="Select Visibility"
              >
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" className={classes.visibilityOwnerDropdown}>
              <InputLabel>Select Owner</InputLabel>
              <Select
                value={selectedOwner}
                placeholder="Select Owner"
                onChange={(event: SelectChangeEvent) => {
                  handleOwnerChange(event as React.ChangeEvent<HTMLInputElement>);
                }}
                label="Select Owner"
              >
                {(adminEmails || []).map((email) => (
                  <MenuItem
                    key={email}
                    value={formatUsername(email)?.toLowerCase()}
                  >
                    {formatUsername(email)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={showActiveStatus}
                  onChange={(e) => {
                    handleStatusChange(e);
                  }}
                  name={showActiveStatus ? 'Show Active' : 'Show InActive'}
                  color="primary"
                />
              }
              label={showActiveStatus ? 'Show Active' : 'Show InActive'}
              sx={{ marginLeft: 2 }}
            />
             {filters?.length ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{ marginTop: 2, marginLeft: '0.70rem' }}
                alignItems="center"
              >
                {filters.map((filter, index) => {
                  return (
                    <Chip
                      key={index}
                      label={<Typography variant="body2" sx={{ color: '#ffffff' }}>
                        <strong>{filter.label}</strong> {filter.value}
                      </Typography>}
                      onDelete={() => {
                        deleteFilter(index);
                      }}
                      className={classes.removeFilterIcon}
                       />
                  );
                })}
                <Typography
                  onClick={handleClearAllFilters}
                  className={classes.clearAllLabel}
                >
                  Clear All
                </Typography>
              </Stack>
            ) : null}
            </>  : 
            <></>}
           
           

            <Divider sx={{ my: 2, borderColor: 'lightGrey' }} />
            <Box className={classes.dataGridContainer}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10]}
                className={classes.customTable}
                hideFooterSelectedRowCount
                sx={{
                  '& .MuiDataGrid-cell': {
                    borderBottom: datagridBottomBorder,
                    fontSize: '12px',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    borderBottom: datagridBottomBorder,
                  },
                  '& .MuiDataGrid-row:last-child .MuiDataGrid-cell': {
                    borderBottom: datagridBottomBorder,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                  },
                }}
              />
            </Box>
          </div>
        </>
      )}
    </div>
  );
};

export default Admin;